import { authService } from "../auth/services/authService";
import { ApiError, type RequestOptions } from "./apiTypes";

const CLIENT_NAME = "FroppioLocal"; // Todo: Make this dynamic or configurable, provided by the host app

export const createApiService = (controllerPath: string) => {
  const baseURL = import.meta.env.VITE_API_URL ?? "http://localhost:3000/api";

  const request = async <T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<T> => {
    const { skipAuth = false, headers = {}, ...restOptions } = options;

    // Build headers
    const requestHeaders: Record<string, string> = {
      "Content-Type": "application/json",
      "X-Client-Id": CLIENT_NAME,
      ...(headers as Record<string, string>),
    };

    // Add auth token if not skipped
    if (!skipAuth) {
      const token = await authService.getAccessToken();
      if (token) {
        requestHeaders["Authorization"] = `Bearer ${token}`;
      }
    }
    // Build full URL
    const path = `${controllerPath}/${endpoint}`.replace(/\/\/+/g, "/");
    const url = `${baseURL}/${path}`;
    console.log("API Request URL:", url);

    const response = await fetch(url.toString(), {
      ...restOptions,
      headers: requestHeaders,
    });

    // Handle response
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      let errorData: unknown;

      try {
        errorData = await response.json();
        if (
          errorData &&
          typeof errorData === "object" &&
          "error" in errorData &&
          typeof errorData.error === "string"
        ) {
          errorMessage = errorData.error;
        } else if (
          errorData &&
          typeof errorData === "object" &&
          "message" in errorData &&
          typeof errorData.message === "string"
        ) {
          errorMessage = errorData.message;
        }
      } catch {
        // If response is not JSON, use status text
      }

      throw new ApiError(errorMessage, response.status, errorData);
    }

    // Parse JSON response
    try {
      return await response.json();
    } catch {
      // If no JSON body, return empty object
      return {} as T;
    }
  };

  return {
    get: async <T>(endpoint: string, options?: RequestOptions): Promise<T> => {
      return request<T>(endpoint, { ...options, method: "GET" });
    },

    post: async <T>(
      endpoint: string,
      data?: unknown,
      options?: RequestOptions
    ): Promise<T> => {
      return request<T>(endpoint, {
        ...options,
        method: "POST",
        body: JSON.stringify(data),
      });
    },

    put: async <T>(
      endpoint: string,
      data?: unknown,
      options?: RequestOptions
    ): Promise<T> => {
      return request<T>(endpoint, {
        ...options,
        method: "PUT",
        body: JSON.stringify(data),
      });
    },

    patch: async <T>(
      endpoint: string,
      data?: unknown,
      options?: RequestOptions
    ): Promise<T> => {
      return request<T>(endpoint, {
        ...options,
        method: "PATCH",
        body: JSON.stringify(data),
      });
    },

    delete: async <T>(
      endpoint: string,
      options?: RequestOptions
    ): Promise<T> => {
      return request<T>(endpoint, { ...options, method: "DELETE" });
    },
  };
};

export type ApiServiceType = ReturnType<typeof createApiService>;
